import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Plus, Edit, Trash2, Box, Layers, X, Image, Upload, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'print', label: 'Photo Print' },
  { value: 'trader_card', label: 'Trader Card' },
  { value: 'memory_mate', label: 'Memory Mate' },
  { value: 'bag_tag', label: 'Bag Tag' },
  { value: 'gift_items', label: 'Gift Items' },
  { value: 'ornament', label: 'Ornament' },
  { value: 'digital', label: 'Digital Download' },
  { value: 'other', label: 'Other' }
];

const CUSTOMIZATION_FIELDS = ['name', 'class', 'year', 'team', 'number', 'position'];

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, product: null, affectedPackages: [] });
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: '',
    price: '',
    cost: '',
    allows_background: false,
    allows_multiple_photos: false,
    max_photos: '',
    customization_fields: [],
    variations: [],
    sample_images: [],
    template_id: ''
  });
  const [uploadingSample, setUploadingSample] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchTemplates();
    fetchPackages();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPackages(response.data);
    } catch (error) {
    }
  };

  const checkProductInPackages = (productId) => {
    return packages.filter(pkg => 
      pkg.items.some(item => item.product_id === productId)
    );
  };

  const openDeleteDialog = (product) => {
    const affectedPackages = checkProductInPackages(product.id);
    setDeleteDialog({ open: true, product, affectedPackages });
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      category: '',
      price: '',
      cost: '',
      allows_background: false,
      allows_multiple_photos: false,
      max_photos: '',
      customization_fields: [],
      variations: [],
      sample_images: [],
      template_id: ''
    });
    setEditingProduct(null);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setForm({
      name: product.name,
      description: product.description || '',
      category: product.category,
      price: product.price.toString(),
      cost: product.cost?.toString() || '',
      allows_background: product.allows_background,
      allows_multiple_photos: product.allows_multiple_photos || false,
      max_photos: product.max_photos?.toString() || '',
      customization_fields: product.customization_fields || [],
      variations: product.variations || [],
      sample_images: product.sample_images || [],
      template_id: product.template_id || ''
    });
    setShowDialog(true);
  };

  // Variation management functions
  const addVariation = () => {
    setForm(prev => ({
      ...prev,
      variations: [...prev.variations, { name: '', required: true, options: [{ name: '', price_adjustment: 0, cost: '', template_id: '' }] }]
    }));
  };

  const removeVariation = (index) => {
    setForm(prev => ({
      ...prev,
      variations: prev.variations.filter((_, i) => i !== index)
    }));
  };

  const updateVariation = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      variations: prev.variations.map((v, i) => i === index ? { ...v, [field]: value } : v)
    }));
  };

  const addVariationOption = (variationIndex) => {
    setForm(prev => ({
      ...prev,
      variations: prev.variations.map((v, i) => 
        i === variationIndex 
          ? { ...v, options: [...v.options, { name: '', price_adjustment: 0, cost: '', template_id: '' }] }
          : v
      )
    }));
  };

  const removeVariationOption = (variationIndex, optionIndex) => {
    setForm(prev => ({
      ...prev,
      variations: prev.variations.map((v, i) => 
        i === variationIndex 
          ? { ...v, options: v.options.filter((_, oi) => oi !== optionIndex) }
          : v
      )
    }));
  };

  const updateVariationOption = (variationIndex, optionIndex, field, value) => {
    setForm(prev => ({
      ...prev,
      variations: prev.variations.map((v, i) => 
        i === variationIndex 
          ? { 
              ...v, 
              options: v.options.map((o, oi) => 
                oi === optionIndex ? { ...o, [field]: value } : o
              )
            }
          : v
      )
    }));
  };

  // Sample image management functions
  const handleSampleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPEG, PNG, WebP');
      return;
    }

    setUploadingSample(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/sample`, formData);
      setForm(prev => ({
        ...prev,
        sample_images: [...prev.sample_images, response.data.url]
      }));
      toast.success('Sample image uploaded');
    } catch (error) {
      toast.error('Failed to upload sample image');
    } finally {
      setUploadingSample(false);
      e.target.value = ''; // Reset input
    }
  };

  const removeSampleImage = (index) => {
    setForm(prev => ({
      ...prev,
      sample_images: prev.sample_images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.category || !form.price) {
      toast.error('Please fill in required fields');
      return;
    }

    // Validate variations
    const validVariations = form.variations.filter(v => v.name && v.options.some(o => o.name));
    const formattedVariations = validVariations.map(v => ({
      name: v.name,
      required: v.required,
      options: v.options.filter(o => o.name).map(o => ({
        name: o.name,
        price_adjustment: parseFloat(o.price_adjustment) || 0,
        cost: o.cost ? parseFloat(o.cost) : null,
        template_id: o.template_id || null
      }))
    }));

    try {
      const data = {
        name: form.name,
        description: form.description || null,
        category: form.category,
        price: parseFloat(form.price),
        cost: form.cost ? parseFloat(form.cost) : null,
        allows_background: form.allows_background,
        allows_multiple_photos: form.allows_multiple_photos,
        max_photos: form.max_photos ? parseInt(form.max_photos) : null,
        customization_fields: form.customization_fields.length > 0 ? form.customization_fields : null,
        variations: formattedVariations.length > 0 ? formattedVariations : null,
        sample_images: form.sample_images.length > 0 ? form.sample_images : null,
        template_id: form.template_id || null
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data);
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/products`, data);
        toast.success('Product created');
      }

      setShowDialog(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.product) return;
    try {
      await axios.delete(`${API}/products/${deleteDialog.product.id}`);
      toast.success('Product deleted');
      setDeleteDialog({ open: false, product: null, affectedPackages: [] });
      fetchProducts();
      fetchPackages(); // Refresh packages list
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const toggleCustomizationField = (field) => {
    setForm(prev => ({
      ...prev,
      customization_fields: prev.customization_fields.includes(field)
        ? prev.customization_fields.filter(f => f !== field)
        : [...prev.customization_fields, field]
    }));
  };

  const moveProduct = async (index, direction) => {
    const newProducts = [...products];
    const newIndex = index + direction;
    
    if (newIndex < 0 || newIndex >= newProducts.length) return;
    
    // Swap products
    [newProducts[index], newProducts[newIndex]] = [newProducts[newIndex], newProducts[index]];
    setProducts(newProducts);
    
    // Save new order to backend
    try {
      await axios.put(`${API}/products/reorder`, newProducts.map(p => p.id));
    } catch (error) {
      toast.error('Failed to save order');
      fetchProducts(); // Revert on error
    }
  };

  const getCategoryLabel = (category) => {
    return CATEGORIES.find(c => c.value === category)?.label || category;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Products</h1>
          <p className="text-slate-600 mt-1">Manage your product catalog and pricing</p>
        </div>
        <Button 
          className="bg-blue-600 hover:bg-blue-700" 
          onClick={() => { resetForm(); setShowDialog(true); }}
          data-testid="new-product-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Product
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-48 bg-slate-200 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : products.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product, index) => (
            <Card key={product.id} className="border-slate-200 card-hover relative">
              {/* Reorder buttons */}
              <div className="absolute top-2 right-2 flex flex-col gap-0.5 z-10">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-slate-200"
                  onClick={() => moveProduct(index, -1)}
                  disabled={index === 0}
                  title="Move up"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-slate-200"
                  onClick={() => moveProduct(index, 1)}
                  disabled={index === products.length - 1}
                  title="Move down"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
              <CardHeader className="pb-3 pr-12">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="font-heading text-lg">{product.name}</CardTitle>
                    <Badge className="mt-2 bg-slate-100 text-slate-700">
                      {getCategoryLabel(product.category)}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-heading font-bold text-slate-900 price-display">
                      {formatCurrency(product.price)}
                    </span>
                    {product.cost && (
                      <p className="text-xs text-green-600">
                        Profit: {formatCurrency(product.price - product.cost)}
                      </p>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {product.description && (
                  <p className="text-sm text-slate-600 mb-4">{product.description}</p>
                )}
                {product.cost && (
                  <div className="mb-3 p-2 bg-slate-50 rounded text-xs">
                    <span className="text-slate-500">Cost: {formatCurrency(product.cost)}</span>
                  </div>
                )}
                <div className="space-y-2 mb-4">
                  {product.allows_background && (
                    <Badge variant="outline" className="text-xs">Background Selection</Badge>
                  )}
                  {product.allows_multiple_photos && (
                    <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">
                      Multi-Photo {product.max_photos ? `(max ${product.max_photos})` : ''}
                    </Badge>
                  )}
                  {product.variations?.length > 0 && (
                    <Badge variant="outline" className="text-xs text-purple-600 border-purple-300">
                      <Layers className="h-3 w-3 mr-1" />
                      {product.variations.length} Variation{product.variations.length > 1 ? 's' : ''}
                    </Badge>
                  )}
                  {product.sample_images?.length > 0 && (
                    <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">
                      <Image className="h-3 w-3 mr-1" />
                      {product.sample_images.length} Sample{product.sample_images.length > 1 ? 's' : ''}
                    </Badge>
                  )}
                  {product.customization_fields?.map(field => (
                    <Badge key={field} variant="outline" className="text-xs mr-1 capitalize">
                      {field}
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => openEditDialog(product)}
                    data-testid={`edit-product-${product.id}`}
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => openDeleteDialog(product)}
                    data-testid={`delete-product-${product.id}`}
                  >
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <Box className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">No products yet</h3>
            <p className="text-slate-500 mb-6">Create your first product to start selling</p>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => { resetForm(); setShowDialog(true); }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Product
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Product Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'New Product'}</DialogTitle>
            <DialogDescription>
              {editingProduct ? 'Update product details' : 'Add a new product to your catalog'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Product Name *</Label>
              <Input
                id="name"
                placeholder="e.g., 8x10 Print"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="product-name-input"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="product-category-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Price (CAD) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  data-testid="product-price-input"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cost">Cost of Goods (CAD)</Label>
              <Input
                id="cost"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
                data-testid="product-cost-input"
              />
              <p className="text-xs text-stone-500">Your cost for profit tracking</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional product description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="product-description-input"
                rows={2}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
              <div>
                <Label htmlFor="background">Allow Background Selection</Label>
                <p className="text-xs text-stone-500">Clients can choose digital backgrounds</p>
              </div>
              <Switch
                id="background"
                checked={form.allows_background}
                onCheckedChange={(checked) => setForm({ ...form, allows_background: checked })}
                data-testid="allows-background-switch"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div>
                <Label htmlFor="multiple_photos">Allow Multiple Photos</Label>
                <p className="text-xs text-stone-500">Clients can select more than one photo</p>
              </div>
              <Switch
                id="multiple_photos"
                checked={form.allows_multiple_photos}
                onCheckedChange={(checked) => setForm({ ...form, allows_multiple_photos: checked, max_photos: checked ? form.max_photos : '' })}
                data-testid="allows-multiple-photos-switch"
              />
            </div>

            {form.allows_multiple_photos && (
              <div className="space-y-2 pl-3 border-l-2 border-blue-200">
                <Label htmlFor="max_photos">Max Photos Allowed</Label>
                <Input
                  id="max_photos"
                  type="number"
                  min="2"
                  placeholder="Leave blank for unlimited"
                  value={form.max_photos}
                  onChange={(e) => setForm({ ...form, max_photos: e.target.value })}
                  data-testid="max-photos-input"
                />
                <p className="text-xs text-stone-500">Minimum 2, or leave empty for no limit</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Customization Fields</Label>
              <div className="flex flex-wrap gap-2">
                {CUSTOMIZATION_FIELDS.map(field => (
                  <Badge
                    key={field}
                    variant={form.customization_fields.includes(field) ? 'default' : 'outline'}
                    className="cursor-pointer capitalize"
                    onClick={() => toggleCustomizationField(field)}
                    data-testid={`customization-${field}`}
                  >
                    {field}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-slate-500">Click to toggle fields clients can customize</p>
            </div>

            {/* Product Variations Section */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-purple-600" />
                    Product Variations
                  </Label>
                  <p className="text-xs text-slate-500">Add options like shape, size, color</p>
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={addVariation}
                  data-testid="add-variation-btn"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Variation
                </Button>
              </div>

              {form.variations.map((variation, vIndex) => (
                <div key={vIndex} className="p-4 border rounded-lg bg-purple-50/50 space-y-3">
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Variation name (e.g., Shape, Size)"
                      value={variation.name}
                      onChange={(e) => updateVariation(vIndex, 'name', e.target.value)}
                      className="flex-1"
                      data-testid={`variation-name-${vIndex}`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeVariation(vIndex)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-slate-600">Options</Label>
                    {variation.options.map((option, oIndex) => (
                      <div key={oIndex} className="flex flex-col gap-2 p-2 bg-white rounded border">
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder="Option name (e.g., Benelux, London)"
                            value={option.name}
                            onChange={(e) => updateVariationOption(vIndex, oIndex, 'name', e.target.value)}
                            className="flex-1"
                            data-testid={`option-name-${vIndex}-${oIndex}`}
                          />
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-500">+$</span>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0"
                              value={option.price_adjustment || ''}
                              onChange={(e) => updateVariationOption(vIndex, oIndex, 'price_adjustment', e.target.value)}
                              className="w-20"
                              data-testid={`option-price-${vIndex}-${oIndex}`}
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <Label className="text-xs text-slate-500">Cost $</Label>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0"
                              value={option.cost || ''}
                              onChange={(e) => updateVariationOption(vIndex, oIndex, 'cost', e.target.value)}
                              className="w-20"
                              data-testid={`option-cost-${vIndex}-${oIndex}`}
                            />
                          </div>
                          {variation.options.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeVariationOption(vIndex, oIndex)}
                              className="text-slate-400 hover:text-red-500 p-1"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        {/* Template selector for each variation option */}
                        {templates.length > 0 && (
                          <div className="flex items-center gap-2 pl-2">
                            <Label className="text-xs text-amber-700 whitespace-nowrap">Template:</Label>
                            <Select
                              value={option.template_id || 'none'}
                              onValueChange={(val) => updateVariationOption(vIndex, oIndex, 'template_id', val === 'none' ? '' : val)}
                            >
                              <SelectTrigger className="h-8 text-xs flex-1">
                                <SelectValue placeholder="Select template" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No Template</SelectItem>
                                {templates.map(t => (
                                  <SelectItem key={t.id} value={t.id}>
                                    <div className="flex items-center gap-2">
                                      <img 
                                        src={`${process.env.REACT_APP_BACKEND_URL}${t.thumbnail_url || t.url}`}
                                        alt={t.name}
                                        className="h-5 w-5 object-contain bg-slate-100 rounded"
                                      />
                                      {t.name}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {option.template_id && (
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${templates.find(t => t.id === option.template_id)?.thumbnail_url}`}
                                alt=""
                                className="h-8 w-8 object-contain bg-slate-100 rounded border"
                              />
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => addVariationOption(vIndex)}
                      className="text-purple-600"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Option
                    </Button>
                  </div>
                </div>
              ))}

              {form.variations.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-2">
                  No variations added. Click "Add Variation" to create options.
                </p>
              )}
            </div>

            {/* Product Template Section */}
            {templates.length > 0 && (
              <div className="space-y-3 p-4 border rounded-lg bg-amber-50/50">
                <Label className="flex items-center gap-2 text-amber-800">
                  <Layers className="h-4 w-4" />
                  Product Template (Shape)
                </Label>
                <p className="text-xs text-amber-600">
                  Link a template to show product shape in preview and for fulfillment
                </p>
                <Select
                  value={form.template_id || 'none'}
                  onValueChange={(val) => setForm({ ...form, template_id: val === 'none' ? '' : val })}
                >
                  <SelectTrigger data-testid="template-select">
                    <SelectValue placeholder="Select a template (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Template</SelectItem>
                    {templates.map(template => (
                      <SelectItem key={template.id} value={template.id}>
                        <div className="flex items-center gap-2">
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${template.thumbnail_url || template.url}`}
                            alt={template.name}
                            className="h-6 w-6 object-contain bg-slate-100 rounded"
                          />
                          {template.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.template_id && (
                  <div className="flex items-center gap-3 p-2 bg-white rounded border border-amber-200">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${templates.find(t => t.id === form.template_id)?.thumbnail_url || templates.find(t => t.id === form.template_id)?.url}`}
                      alt="Selected template"
                      className="h-16 w-16 object-contain bg-slate-100 rounded"
                    />
                    <div className="text-sm">
                      <p className="font-medium">{templates.find(t => t.id === form.template_id)?.name}</p>
                      <p className="text-xs text-slate-500">
                        {templates.find(t => t.id === form.template_id)?.width_px} x {templates.find(t => t.id === form.template_id)?.height_px}px
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Sample Images Section */}
            <div className="space-y-3 p-4 border rounded-lg bg-blue-50/50">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-blue-800">
                  <Image className="h-4 w-4" />
                  Sample Images
                </Label>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleSampleImageUpload}
                    className="hidden"
                    disabled={uploadingSample}
                    data-testid="sample-image-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploadingSample}
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    onClick={(e) => e.currentTarget.previousElementSibling?.click()}
                    data-testid="add-sample-image-btn"
                  >
                    {uploadingSample ? (
                      <span className="animate-pulse">Uploading...</span>
                    ) : (
                      <>
                        <Upload className="h-3 w-3 mr-1" />
                        Add Image
                      </>
                    )}
                  </Button>
                </label>
              </div>
              <p className="text-xs text-blue-600">
                Upload photos showing what this product looks like (e.g., sample mug, ornament, print)
              </p>
              
              {form.sample_images.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {form.sample_images.map((url, index) => (
                    <div key={url || `sample-${index}`} className="relative group">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                        alt={`Sample ${index + 1}`}
                        className="w-full aspect-square object-cover rounded-lg border border-blue-200"
                        data-testid={`sample-image-${index}`}
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => removeSampleImage(index)}
                        data-testid={`remove-sample-${index}`}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-4 border-2 border-dashed border-blue-200 rounded-lg">
                  No sample images added. Clients will only see the product name.
                </p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-product-btn">
                {editingProduct ? 'Update' : 'Create'} Product
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, product: null, affectedPackages: [] })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Product</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteDialog.product?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {/* Warning about affected packages */}
          {deleteDialog.affectedPackages?.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-medium text-amber-800 mb-2">
                This product is included in {deleteDialog.affectedPackages.length} package(s):
              </p>
              <ul className="text-sm text-amber-700 space-y-1">
                {deleteDialog.affectedPackages.map(pkg => (
                  <li key={pkg.id} className="flex items-center gap-2">
                    <span className="font-medium">{pkg.name}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-amber-600 mt-2">
                Deleting this product will remove it from these packages. Consider updating the packages first.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, product: null, affectedPackages: [] })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-product-btn">
              Delete Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
